import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { AnswersSummary } from '@/components/AnswersSummary';
import {
  HeartHandshake, LogOut, ChevronLeft, Loader2, Send,
  MessageSquare, User, Inbox
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function AllyDashboard() {
  const auth = useAuth();

  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // leader_id
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [posting, setPosting] = useState(false);

  const loadLeaders = useCallback(async function () {
    setLoading(true);
    try {
      const res = await fetch(API_URL + '/api/ally/leaders', { credentials: 'include' });
      if (res.ok) {
        setLeaders(await res.json());
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function () { loadLeaders(); }, [loadLeaders]);

  async function openLeader(leaderId) {
    setSelected(leaderId);
    setDetailLoading(true);
    setDetail(null);
    setNotes([]);
    try {
      const [dRes, nRes] = await Promise.all([
        fetch(API_URL + '/api/ally/leader/' + leaderId, { credentials: 'include' }),
        fetch(API_URL + '/api/ally/leader/' + leaderId + '/notes', { credentials: 'include' })
      ]);
      if (dRes.ok) setDetail(await dRes.json());
      if (nRes.ok) setNotes(await nRes.json());
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  }

  async function postNote() {
    if (!noteText.trim()) return;
    setPosting(true);
    try {
      const res = await fetch(API_URL + '/api/ally/leader/' + selected + '/notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: noteText.trim() })
      });
      if (res.ok) {
        const note = await res.json();
        setNotes(function (prev) { return [note].concat(prev); });
        setNoteText('');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setPosting(false);
    }
  }

  function formatDate(iso) {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
    catch (e) { return iso; }
  }

  return (
    <div className="min-h-screen bg-background" data-testid="ally-dashboard">
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <HeartHandshake className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="font-serif font-bold text-foreground leading-tight">Ally Check-In</h1>
              <p className="text-xs text-muted-foreground">{auth.user ? auth.user.email : ''}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={auth.logout} data-testid="ally-logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* List view */}
        {!selected && (
          <div data-testid="ally-leaders-list">
            <h2 className="font-serif text-xl font-bold mb-1">People you support</h2>
            <p className="text-sm text-muted-foreground mb-6">Check in on their progress and leave encouragement.</p>

            {loading ? (
              <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
            ) : leaders.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground" data-testid="ally-empty">
                <Inbox className="h-8 w-8 mx-auto mb-3 opacity-60" />
                You're not linked to anyone yet.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {leaders.map(function (l) {
                  return (
                    <button
                      key={l.leader_id}
                      onClick={function () { openLeader(l.leader_id); }}
                      className="text-left bg-card border border-border rounded-xl p-5 hover:border-primary/50 transition-colors shadow-card"
                      data-testid={'leader-card-' + l.leader_id}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <User className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <span className="font-medium text-sm truncate">{l.leader_email}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: l.progress_percent + '%' }} />
                        </div>
                        <span className="text-sm font-medium">{l.progress_percent}%</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Detail view */}
        {selected && (
          <div data-testid="ally-leader-detail">
            <Button variant="ghost" size="sm" onClick={function () { setSelected(null); }} className="mb-4 -ml-2" data-testid="ally-back-button">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>

            {detailLoading ? (
              <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
            ) : detail ? (
              <div className="space-y-8">
                <div className="bg-card border border-border rounded-xl p-5 shadow-card">
                  <h2 className="font-serif text-xl font-bold mb-1">{detail.leader_email}</h2>
                  <div className="flex items-center gap-3 mt-3">
                    <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden max-w-xs">
                      <div className="h-full bg-primary rounded-full" style={{ width: detail.progress_percent + '%' }} />
                    </div>
                    <span className="text-sm font-medium">{detail.progress_percent}% complete</span>
                  </div>
                  {detail.updated_at && (
                    <p className="text-xs text-muted-foreground mt-2">Last updated {formatDate(detail.updated_at)}</p>
                  )}
                </div>

                {/* Check-in notes */}
                <div>
                  <h3 className="font-serif text-lg font-bold mb-3 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" /> Check-In Notes
                  </h3>
                  <div className="bg-card border border-border rounded-xl p-4 mb-4 shadow-card">
                    <Textarea
                      placeholder="Leave a note of encouragement or a question..."
                      value={noteText}
                      onChange={function (e) { setNoteText(e.target.value); }}
                      rows={3}
                      data-testid="note-input"
                    />
                    <div className="flex justify-end mt-3">
                      <Button onClick={postNote} disabled={posting || !noteText.trim()} size="sm" data-testid="post-note-button">
                        {posting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                        Send Note
                      </Button>
                    </div>
                  </div>
                  {notes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No notes yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {notes.map(function (n) {
                        return (
                          <div key={n.id} className="bg-muted/40 border border-border rounded-lg p-3" data-testid={'note-' + n.id}>
                            <p className="text-sm text-foreground whitespace-pre-wrap">{n.text}</p>
                            <p className="text-xs text-muted-foreground mt-2">{n.ally_email} · {formatDate(n.created_at)}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Answers */}
                <div>
                  <h3 className="font-serif text-lg font-bold mb-3">Their Workbook</h3>
                  <AnswersSummary data={detail.data} />
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Could not load this person's data.</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export { AllyDashboard };
